import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { eventTrack } from "../../utils/gtag";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../utils/dateUtils";

export interface InfoBullProps {
  description: string;
  lastUpdated?: string;
  loading?: boolean;
  isUnavailable?: boolean;
  error?: boolean | null;
  onOpenTracking?: () => void;
}

export const InfoBull: React.FC<InfoBullProps> = ({
  description,
  lastUpdated,
  loading = false,
  isUnavailable = false,
  error = false,
  onOpenTracking,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);
  const popupRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<DOMRect | null>(null);
  const [isLeftAligned, setIsLeftAligned] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const togglePopup = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setCoords(rect);
      setIsLeftAligned(rect.left < 300);
    }
    if (!open) {
      eventTrack({
        eventName: "kpi_info",
        actionName: "Show Kpi dateTime",
        key1: "kpi_name",
        value1: description,
      });
      if (onOpenTracking) {
        onOpenTracking();
      }
    }
    setOpen(!open);
  };

  const popupContent = open &&
    coords && (
      <span
        ref={popupRef}
        style={{
          position: "absolute",
          top: `${coords.top + window.scrollY}px`,
          left: `${coords.left + window.scrollX +10}px`,
          width: `${coords.width}px`,
          height: `${coords.height}px`
        }}
      >
        <span
          role="dialog"
          style={{
            position: "absolute",
            bottom: "100%",
              right: isLeftAligned ? "auto" : 1,
              left: isLeftAligned ? "-50px" : "auto",
            width: "260px",
            padding: "12px",
            marginBottom: "10px",
            background: "#000000", // black background
            color: "#ffffff",
            border: "1px solid #ddd",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 1000,
            fontSize: "14px",
            textAlign: "left",
            fontWeight: "normal",
            fontFamily: "inherit",
            display: "block",
          }}
        >
          <span style={{ display: "block", marginBottom: "8px" }}>
            <strong> {t('info_bull.lab_definition')}:</strong>
          </span>
          <span style={{ display: "block" }}>{description}</span>
          <br />

          {t('info_bull.lab_last_update')}

          {loading && !isUnavailable ? (
            <span>
              {t('info_bull.msg_refreshing')}
            </span>
          ) : (error || !lastUpdated || lastUpdated?.includes("Error")) ? (
            <span>
              {t('info_bull.msg_nodata')}
            </span>
          ) : (
            <span>{formatDate(lastUpdated)}</span>
          )}

          <span  style={{ display: "block" }}>
            {/** @TODO: add link of kpi */}
            <label style={{ color: 'white', textDecoration: 'underline' }}>{t('info_bull.link')}</label>
          </span>

          <span
            style={{
              position: "absolute",
              bottom: "-6px",
              right: isLeftAligned ? "auto" : `${coords.width / 2 + 2}px`,
              left: isLeftAligned ? `${coords.width / 2 + 34}px` : "auto",
              width: "12px",
              height: "12px",
              background: "#000000",
              transform: "rotate(45deg)",
              borderRight: "1px solid #ddd",
              borderBottom: "1px solid #ddd",
            }}
          />
        </span>
      </span>
    );

  return (
    <span
      ref={containerRef}
      style={{ position: "relative", display: "inline-block", marginRight: "1mm" }}
      data-testid="info-bull"
    >
      <button
        onClick={togglePopup}
        aria-label="Information"
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="11"
            stroke="#2f80ed"
            strokeWidth="2"
            fill="white"
          />
          <line
            x1="12"
            y1="10"
            x2="12"
            y2="17"
            stroke="#2f80ed"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="12" cy="7" r="1.5" fill="#2f80ed" />
        </svg>
      </button>

      {open && coords && createPortal(popupContent, document.body)}
    </span>
  );
};